import { useEffect, useLayoutEffect, useRef } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

// Own scroll positioning from the very first paint — before any React
// route effect runs. Prevents the browser's default scroll-restoration
// from momentarily jumping the page before our own restore kicks in.
if (typeof window !== "undefined" && "scrollRestoration" in window.history) {
  window.history.scrollRestoration = "manual";
}

/**
 * ScrollToTop — the site's owner of scroll position across route
 * changes.
 *
 * Highlights over the naïve "save window.scrollY, later restore it"
 * approach:
 *
 *   1. ANCHOR-BASED RESTORE. Any element on the page can opt-in with
 *      `data-scroll-anchor="<stable-key>"` (e.g. product cards on the
 *      collection grid tag themselves with the product slug). When we
 *      save scroll for a path we ALSO snapshot the topmost visible
 *      anchor + its distance from the top of the viewport. On restore
 *      we look that anchor up in the DOM and align it back to the same
 *      viewport offset — so if fonts or header images shift the layout
 *      between save and restore, we still land on the card the user was
 *      looking at (not "1400px from the top", which now points 60px
 *      down the next row).
 *
 *   2. CONTINUOUS TRACKER. A rAF-throttled scroll listener continuously
 *      records the current scrollY (and topmost anchor) into
 *      sessionStorage keyed by the current pathname. A capture-phase
 *      pointerdown/touchstart also flushes a save synchronously — so
 *      the correct value is written BEFORE any navigation click can
 *      trigger, avoiding the classic bug where the destination route's
 *      shorter DOM clamps window.scrollY before we get to read it.
 *
 *   3. RETRY LOOP. Restoration keeps re-applying the target across a
 *      rAF loop plus a ResizeObserver on documentElement + body, for up
 *      to 5 s. Handles the case where the collection grid hasn't
 *      finished laying out its images at the moment we try to jump to
 *      the saved scroll depth.
 *
 *   4. BFCACHE RESUME. `pageshow` with `event.persisted === true`
 *      re-runs restoration outside React (since Safari iOS commonly
 *      revives the page from bfcache without invoking React effects).
 */

const KEY = "gl_scroll_positions_v4";

const readMap = () => {
  try {
    return JSON.parse(sessionStorage.getItem(KEY) || "{}");
  } catch (_e) {
    return {};
  }
};
const writeMap = (m) => {
  try {
    sessionStorage.setItem(KEY, JSON.stringify(m));
  } catch (_e) {
    /* private mode / quota — safely ignore */
  }
};

/**
 * Find the topmost `[data-scroll-anchor]` element that is currently at
 * or below y=0 in the viewport. Ignores anchors that are entirely
 * scrolled off the top. Returns `{ key, offset }` (offset = the
 * anchor's distance from the top of the viewport at capture time) or
 * `null` if no anchors are on the page.
 */
const captureTopAnchor = () => {
  const anchors = document.querySelectorAll("[data-scroll-anchor]");
  if (!anchors.length) return null;
  // Small negative tolerance so a card whose top is JUST above the
  // fold (say, -10px) still counts as "the one the user is looking at"
  // rather than the next row.
  const MIN = -40;
  for (const el of anchors) {
    const rect = el.getBoundingClientRect();
    if (rect.top >= MIN) {
      return {
        key: el.getAttribute("data-scroll-anchor"),
        offset: rect.top,
      };
    }
  }
  return null;
};

const savePath = (pathname, y, anchor) => {
  if (!pathname) return;
  const map = readMap();
  map[pathname] = { y, anchor: anchor || null };
  writeMap(map);
};

const savePathAuto = (pathname) => {
  savePath(pathname, window.scrollY, captureTopAnchor());
};

/** Given a stored { y, anchor } entry, compute the effective target y. */
const targetFromEntry = (entry) => {
  if (!entry) return 0;
  if (entry.anchor?.key) {
    // Escape any characters that would break the CSS selector.
    const key = entry.anchor.key.replace(/(["\\])/g, "\\$1");
    const el = document.querySelector(`[data-scroll-anchor="${key}"]`);
    if (el) {
      const rect = el.getBoundingClientRect();
      const currentTop = rect.top + window.scrollY;
      return Math.max(0, Math.round(currentTop - entry.anchor.offset));
    }
  }
  return typeof entry.y === "number" ? entry.y : 0;
};

/**
 * Keep re-applying scrollTo(target) until:
 *   • total elapsed exceeds MAX_MS (5 s), OR
 *   • the user scrolls manually (>40 px delta vs our last write).
 *
 * We DELIBERATELY do NOT bail out early on "we've landed for two
 * frames" — content-visibility skipped rows can collapse from their
 * intrinsic-size placeholder to their real (smaller) height a few
 * hundred ms after we align. The retry loop must stay live long
 * enough to chase the anchor across those late shifts. The target is
 * recomputed EVERY tick from the stored entry, so if the anchor
 * element's absolute position moves between two frames (image load,
 * font swap, off-screen row collapse), we snap to the new position.
 */
const MAX_MS = 5000;
const HOLD_LANDED_MS = 1500; // Keep polling this long AFTER first successful land.

const restoreScroll = (entry) => {
  const initialTarget = targetFromEntry(entry);
  if (!initialTarget || initialTarget <= 0) {
    window.scrollTo(0, 0);
    return;
  }
  const start = performance.now();
  let firstLandedAt = 0;
  let lastAppliedY = 0;
  let stopped = false;
  let ro = null;

  const stop = () => {
    stopped = true;
    ro?.disconnect();
    window.removeEventListener("scroll", onUser, { passive: true });
    window.removeEventListener("wheel", onUser, { passive: true });
    window.removeEventListener("touchmove", onUser, { passive: true });
  };

  const onUser = () => {
    if (Math.abs(window.scrollY - lastAppliedY) > 40) stop();
  };

  const tick = () => {
    if (stopped) return;
    const now = performance.now();
    if (now - start > MAX_MS) return stop();

    const top = targetFromEntry(entry);
    const maxReachable =
      Math.max(
        document.documentElement.scrollHeight,
        document.body?.scrollHeight || 0
      ) - window.innerHeight;
    const target = Math.min(top, Math.max(0, maxReachable));

    if (Math.abs(window.scrollY - target) > 2) {
      window.scrollTo(0, target);
      lastAppliedY = target;
    } else if (target >= top - 2) {
      // Landed. Note the first-land timestamp so we can gracefully
      // stop after HOLD_LANDED_MS of stability.
      if (!firstLandedAt) firstLandedAt = now;
      if (now - firstLandedAt >= HOLD_LANDED_MS) return stop();
    }
    requestAnimationFrame(tick);
  };

  window.addEventListener("scroll", onUser, { passive: true });
  window.addEventListener("wheel", onUser, { passive: true });
  window.addEventListener("touchmove", onUser, { passive: true });

  if ("ResizeObserver" in window) {
    ro = new ResizeObserver(() => {
      if (!stopped) tick();
    });
    ro.observe(document.documentElement);
    if (document.body) ro.observe(document.body);
  }

  tick();
  requestAnimationFrame(tick);
  setTimeout(tick, 60);
  setTimeout(tick, 200);
  setTimeout(tick, 600);
  setTimeout(tick, 1400);
  setTimeout(tick, 2800);
};

const ScrollToTop = () => {
  const { pathname } = useLocation();
  const navType = useNavigationType();
  const currentPathRef = useRef(pathname);
  const rafRef = useRef(0);

  // 1) Continuous throttled tracker + capture-phase pointer flush.
  useEffect(() => {
    const onScroll = () => {
      if (rafRef.current) return;
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = 0;
        savePathAuto(currentPathRef.current);
      });
    };
    const onPointer = () => {
      savePathAuto(currentPathRef.current);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("pointerdown", onPointer, { capture: true, passive: true });
    window.addEventListener("touchstart", onPointer, { capture: true, passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("pointerdown", onPointer, { capture: true });
      window.removeEventListener("touchstart", onPointer, { capture: true });
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  // 2) Route-change handler. Do NOT overwrite the outgoing route's
  //    saved scroll here — the destination may have already clamped
  //    window.scrollY. Trust the continuous tracker; only handle
  //    nav-type intent.
  useLayoutEffect(() => {
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }
    currentPathRef.current = pathname;

    if (navType === "POP") {
      const entry = readMap()[pathname];
      restoreScroll(entry);
    } else if (navType === "PUSH") {
      window.scrollTo(0, 0);
    }
    // REPLACE — preserve current scroll.
  }, [pathname, navType]);

  // 3) Persistent listeners for save-on-leave and bfcache restore.
  useEffect(() => {
    const onSave = () => {
      savePathAuto(window.location.pathname);
    };
    const onPageShow = (e) => {
      if (!e.persisted) return;
      const entry = readMap()[window.location.pathname];
      if (entry) restoreScroll(entry);
    };
    window.addEventListener("pagehide", onSave);
    window.addEventListener("visibilitychange", onSave);
    window.addEventListener("pageshow", onPageShow);
    return () => {
      window.removeEventListener("pagehide", onSave);
      window.removeEventListener("visibilitychange", onSave);
      window.removeEventListener("pageshow", onPageShow);
    };
  }, []);

  return null;
};

export default ScrollToTop;
